import {
  Button,
  CircularProgress,
  InputLabel,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import './style.css';
import {
  ImageItem,
  handlePersistedFiles,
  persistFiles,
  removePersistedFiles,
} from '../../../utils/form/appointmentForm';
import { isImage, processImage } from '../../../utils/image';

interface ImageInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  images?: ImageItem[];
  setImages: React.Dispatch<React.SetStateAction<ImageItem[]>>;
  persistImages?: boolean;
  maxImages?: number;
}

async function dataUrlToJpegFile(dataUrl: string, id: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], `${id}.jpg`, { type: 'image/jpeg' });
}

function ImageInput({
  name,
  label,
  placeholder,
  images,
  setImages,
  persistImages,
  maxImages,
}: ImageInputProps) {
  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const showError = React.useCallback((message: string) => {
    setHasError(true);
    toast.error(message, {
      autoClose: 10_000,
    });
  }, []);

  React.useEffect(() => {
    // only load persisted files if no files are already present
    if (images?.length || !persistImages) return;
    const getPersistedFiles = async () => {
      const newImgs = await handlePersistedFiles();
      if (newImgs.length) setImages(newImgs);
    };
    getPersistedFiles();
  }, [images, setImages, persistImages]);

  const onInputChange = React.useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;

      const imageFiles = Array.from(files).filter(isImage);
      if (!imageFiles.length) {
        showError('Please select a JPG, PNG, WebP, or HEIC image.');
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      setIsProcessing(true);
      setHasError(false);
      try {
        const dataUrls = await Promise.all(imageFiles.map(processImage));
        const newImgs = dataUrls.map(url => ({
          id: Math.random().toString(36).substring(7),
          url,
        }));

        setImages(c => [...(c ?? []), ...newImgs]);
        if (persistImages) {
          const jpegFiles = await Promise.all(
            newImgs.map(img => dataUrlToJpegFile(img.url, img.id)),
          );
          await persistFiles(newImgs, jpegFiles);
        }
      } catch (err) {
        showError(
          err instanceof Error ? err.message : 'Failed to process image',
        );
      } finally {
        setIsProcessing(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [setImages, persistImages, showError],
  );

  const onRemoveImage = React.useCallback(
    async (imageId: string) => {
      const newImgs = images?.filter(img => img.id !== imageId);
      setImages(newImgs ?? []);
      setHasError(false);
      if (persistImages) removePersistedFiles([imageId]);
    },
    [images, setImages, persistImages],
  );

  return (
    <div className={`input image-upload-input${hasError ? ' error' : ''}`}>
      {!!label && (
        <InputLabel htmlFor={name}>
          <Typography variant="h5">{label}</Typography>
        </InputLabel>
      )}

      <div className="image-input-container">
        {(maxImages === undefined || (images?.length ?? 0) < maxImages) && (
          <Button
            variant="outlined"
            type="button"
            className="trigger-button"
            disabled={isProcessing}
            onClick={() => inputRef.current?.click()}>
            {isProcessing ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <>
                <AddIcon color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  align="center">
                  {maxImages === 1 ? 'Add Photo' : 'Add Photos'}
                </Typography>
              </>
            )}
          </Button>
        )}

        <div className="images-list">
          {images?.map(image => {
            if (!image?.url) return null;
            return (
              <div key={image.id} className="image-upload-preview">
                <img src={image.url} alt="img" />
                <button
                  className="remove-image"
                  onClick={() => onRemoveImage(image.id)}
                  type="button">
                  <DeleteIcon
                    fontSize="small"
                    htmlColor={theme.palette.darkGrey.main}
                  />
                </button>
              </div>
            );
          })}
        </div>

        <input
          id={name}
          type="file"
          accept="image/jpeg, image/png, image/webp, .jpg, .jpeg, .png, .webp, .heic, .heif"
          multiple={maxImages !== 1}
          style={{ display: 'none' }}
          name={name}
          placeholder={placeholder}
          ref={inputRef}
          onChange={e => onInputChange(e.target.files)}
        />
      </div>
    </div>
  );
}

export default ImageInput;
